import { Link, type LinkProps } from "react-router-dom";
import { useLocalizedUrl } from "@/hooks/use-localized-routes";
import type { LanguageCode } from "@/contexts/LanguageContext";

interface LocalizedLinkProps extends Omit<LinkProps, "to"> {
  /** Canonical (English) path, e.g. "/experiences/kayak-tour" */
  to: string;
  /** Override the target language (defaults to current language) */
  lang?: LanguageCode;
  children: React.ReactNode;
}

/**
 * A React Router Link that automatically localizes the `to` prop
 * based on the current language and localized slug mappings.
 */
export function LocalizedLink({ to, lang, children, ...props }: LocalizedLinkProps) {
  const { localizeUrl } = useLocalizedUrl();
  const href = localizeUrl(to, lang);

  return (
    <Link to={href} {...props}>
      {children}
    </Link>
  );
}

/**
 * An anchor link variant for non-SPA navigation.
 */
interface LocalizedAnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  lang?: LanguageCode;
  children: React.ReactNode;
}

export function LocalizedAnchor({ to, lang, children, ...props }: LocalizedAnchorProps) {
  const { localizeUrl } = useLocalizedUrl();
  const href = localizeUrl(to, lang);

  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}
